import { Col, FormGroup, FormSelect, InputGroup, Row, Tab, Tabs } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { IComponentProperty, IContentComponent, IEditorSiteFrame } from "../../../models/Interfaces";
import { frameForProperty } from "../../components/common/editorSiteFrame";
import BodyTextEditor from "./propertyeditors/BodyTextEditor";
import StringEditor from "./propertyeditors/StringEditor";
import DateEditor from "./propertyeditors/DateEditor";
import CheckedEditor from "./propertyeditors/CheckedEditor";
import ColourPicker from "../../components/common/ColourPicker";
import DecimalNumberEditor from "./propertyeditors/DecimalNumberEditor";
import WholeNumberEditor from "./propertyeditors/WholeNumberEditor";
import ListEditor from "./propertyeditors/ListEditor";
import LinkTargetEditor from "./propertyeditors/LinkTargetEditor";
import ImageSelectEditor from "./propertyeditors/ImageSelectEditor";
import EmbeddedEditor from "./propertyeditors/EmbeddedEditor";
import PageSelectEditor from "./propertyeditors/PageSelectEditor";
import SelectOptionsEditor from "./propertyeditors/SelectOptionsEditor";
import AccordionEditor from "./propertyeditors/AccordionEditor";
import FormFieldsEditor from "./propertyeditors/FormFieldsEditor";
import CarouselEditor from "./propertyeditors/CarouselEditor";
import TabbedContentEditor from "./propertyeditors/TabbedContentEditor";
import HTMLBlockEditor from "./propertyeditors/HTMLBlockEditor";
import { actionEmitter, EmitEvent } from "../../../config/ActionEmitter";

interface INewsArticleEditor {
  component: IContentComponent;
  editorFrame?: IEditorSiteFrame;
  onValueChange: (propertyid: number, value: string) => void;
  onColSpanChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onEditorOpen: (open: boolean) => void;
}

const ArticleTabs = {
  Title: "Title",
  Intro: "Intro",
  Body: "Body",
  Extended: "Extended"
};

const editorComponents = {
  BodyText: BodyTextEditor,
  String: StringEditor,
  Date: DateEditor,
  Checked: CheckedEditor,
  Colour: ColourPicker,
  ColourPicker: ColourPicker,
  DecimalNumber: DecimalNumberEditor,
  WholeNumber: WholeNumberEditor,
  List: ListEditor,
  LinkTarget: LinkTargetEditor,
  Image: ImageSelectEditor,
  Embedded: EmbeddedEditor,
  PageSelector: PageSelectEditor,
  SelectOptions: SelectOptionsEditor,
  AccordionItems: AccordionEditor,
  FormFields: FormFieldsEditor,
  Carousel: CarouselEditor,
  TabItems: TabbedContentEditor,
  HtmlItems: HTMLBlockEditor
};

type PropertyTypeName = keyof typeof editorComponents;

const resolveEditorComponent = (typeName: string) => {
  return editorComponents[typeName as PropertyTypeName]
    ?? Object.entries(editorComponents).find(([name]) => name.toLowerCase() === typeName.toLowerCase())?.[1];
};

const primaryPropertyNames = [
  "title",
  "summaryimage",
  "author",
  "summarylinkbuttontext",
  "intro",
  "body"
];

const NewsArticleEditor = (props: INewsArticleEditor): JSX.Element => {
  const [activeTab, setActiveTab] = useState<string>(ArticleTabs.Title);

  const sortedProperties = useMemo(() => {
    return [...props.component.BaseComponent.Properties].sort((a, b) => a.EditorDisplayOrder - b.EditorDisplayOrder);
  }, [props.component.BaseComponent.Properties]);

  const renderPropertyEditor = (prop: IComponentProperty, editorHeight?: number): JSX.Element | null => {
    if (!prop.UserEditable) {
      return null;
    }

    const EditorComponent = resolveEditorComponent(prop.Type.Name);

    if (!EditorComponent) {
      return null;
    }

    return (
      <FormGroup>
        <EditorComponent
          startEditing={true}
          property={prop}
          onValueChange={props.onValueChange}
          onEditorOpen={props.onEditorOpen}
          editorHeight={editorHeight}
          siteFrame={frameForProperty(props.editorFrame, props.component, prop)}
        />
      </FormGroup>
    );
  };

  const findProperty = (name: string): IComponentProperty | undefined => {
    return sortedProperties.find((prop) => prop.PropertyName.toLowerCase() === name.toLowerCase());
  };

  const renderNamedProperty = (name: string, cols = 12, editorHeight?: number): JSX.Element | null => {
    const prop = findProperty(name);

    if (!prop) {
      return null;
    }

    return (
      <Col md={12} xl={cols} key={prop.Id} className="mt-3">
        {renderPropertyEditor(prop, editorHeight)}
      </Col>
    );
  };

  const renderExtendedProperties = (): Array<JSX.Element> => {
    return sortedProperties
      .filter((prop) => prop.ShowOnExtendedTab && prop.UserEditable)
      .map((prop) => (
        <Col md={12} xl={6} key={prop.Id} className="mt-3">
          {renderPropertyEditor(prop)}
        </Col>
      ));
  };

  const renderAdditionalProperties = (): Array<JSX.Element> => {
    return sortedProperties
      .filter((prop) =>
        prop.UserEditable
        && !prop.ShowOnExtendedTab
        && !primaryPropertyNames.includes(prop.PropertyName.toLowerCase()))
      .map((prop) => (
        <Col md={12} xl={6} key={prop.Id} className="mt-3">
          {renderPropertyEditor(prop)}
        </Col>
      ));
  };

  const onCopyApiKey = (): void => {
    navigator.clipboard.writeText(props.component.ApiTag);
    actionEmitter.emit(EmitEvent.Result, true, `${props.component.ApiTag} copied to clipboard`);
  };

  return (
    <Tabs
      id={`news-article-properties-${props.component.Id}`}
      activeKey={activeTab}
      onSelect={(tk) => setActiveTab(tk ?? ArticleTabs.Title)}
      className="m-0 p-0 mt-2"
    >
      <Tab eventKey={ArticleTabs.Title} title={ArticleTabs.Title} className="ms-2 me-2">
        <Row>
          {renderNamedProperty("Title", 12, 320)}
          {renderNamedProperty("SummaryImage", 12)}
          {renderNamedProperty("Author", 6)}
          {renderNamedProperty("SummaryLinkButtonText", 6)}
        </Row>
      </Tab>

      <Tab eventKey={ArticleTabs.Intro} title={ArticleTabs.Intro} className="ms-2 me-2">
        <Row>
          {renderNamedProperty("Intro", 12, 460)}
        </Row>
      </Tab>

      <Tab eventKey={ArticleTabs.Body} title={ArticleTabs.Body} className="ms-2 me-2">
        <Row>
          {renderNamedProperty("Body", 12, 650)}
        </Row>
      </Tab>

      <Tab eventKey={ArticleTabs.Extended} title={ArticleTabs.Extended} className="ms-2 me-2">
        <Row>
          {renderExtendedProperties()}
          {renderAdditionalProperties()}
        </Row>

        <Row>
          <Col xs={12}>
            <hr />
          </Col>
        </Row>

        <Row>
          <Col md={12} xl={6} className="mt-3">
            <InputGroup>
              <InputGroupText>Col Span</InputGroupText>
              <FormSelect
                value={props.component.ColSpan}
                title="set column span (if section uses columns)"
                onChange={props.onColSpanChange}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                  <option key={i} value={i}>{`${i}`} {i === 1 ? "Column" : "Columns"}</option>
                ))}
              </FormSelect>
            </InputGroup>
          </Col>
          <Col md={12} xl={6} className="mt-3">
            <InputGroup>
              <InputGroupText>Api Key</InputGroupText>
              <input
                className="form-control"
                type="text"
                value={props.component.ApiTag}
                readOnly={true}
              />
              <InputGroupText title="copy api key" style={{ cursor: "pointer" }} onClick={onCopyApiKey}>
                <FontAwesomeIcon icon={faCopy} />
              </InputGroupText>
            </InputGroup>
          </Col>
        </Row>
      </Tab>
    </Tabs>
  );
};

export default NewsArticleEditor;
